import React from 'react';
import _ from 'underscore';

import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {NucleotideType} from 'biojs/BioConstants';
import SpreadsheetBulkImportStore from 'bulk-import/SpreadsheetBulkImportStore';
import ModelTypeConstants, {
  LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE,
  OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE,
} from 'constants/ModelTypeConstants';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {NucleotideTypeToggle} from 'molbio/NucleotideTypeToggle';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {typeToPluralized} from 'util/NameUtil';

import * as SpreadsheetBulkImportActions from '../SpreadsheetBulkImportActions';
import {OPTIONS, SelectedSchemaIds} from '../SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportOption from '../SpreadsheetBulkImportOption';
import {getTagSchema, isSequenceSchema} from '../SpreadsheetBulkImportUtil';

function getInputContainableType({
  options,
  selectedSchemaIds,
}: {
  options: {[key in OPTIONS]: boolean};
  selectedSchemaIds: SelectedSchemaIds;
}): string | null {
  const tagSchema = getTagSchema(selectedSchemaIds);
  if (tagSchema && tagSchema.isContainable) {
    return ModelTypeConstants.BIOENTITY_MIXIN;
  }
  return null;
}

export function getEnabledBioentityImportOptions({
  options,
  selectedSchemaIds,
  schemalessCreationEntityType,
}: {
  options: {[key in OPTIONS]: boolean};
  selectedSchemaIds: SelectedSchemaIds;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
}): Array<OPTIONS> {
  const enabledOptions = [];
  const {tagSchemaId} = selectedSchemaIds;

  if (
    isFeatureEnabled({feature: InventoryFeature.INVENTORY_IMPORT}) &&
    getInputContainableType({options, selectedSchemaIds})
  ) {
    enabledOptions.push(OPTIONS.IMPORT_CONTAINERS);
  }

  if (
    (tagSchemaId != null && isSequenceSchema(tagSchemaId)) ||
    schemalessCreationEntityType === ModelTypeConstants.SEQUENCE
  ) {
    enabledOptions.push(OPTIONS.ARE_SEQUENCES_CIRCULAR);
  }

  return enabledOptions;
}

interface ImportContainersOptionProps {
  options: {[key in OPTIONS]: boolean};
  selectedSchemaIds: SelectedSchemaIds;
}

class ImportContainersOption extends React.Component<ImportContainersOptionProps> {
  _handleChangeContainerSchema = (selectedContainerSchema: ContainerSchema): void => {
    SpreadsheetBulkImportActions.setImportSetting({selectedContainerSchemaId: selectedContainerSchema.id});
  };

  render(): JSX.Element {
    const {options, selectedSchemaIds} = this.props;
    const {containerSchemaId, tagSchemaId} = selectedSchemaIds;
    const inputContainableType = getInputContainableType(this.props) || ModelTypeConstants.BATCH;
    return (
      <div>
        <SpreadsheetBulkImportOption
          helpContent={
            <div>
              If this option is selected, the new {typeToPluralized(inputContainableType)} will be transferred
              to containers. If the containers do not exist, they will be created.
            </div>
          }
          isChecked={options[OPTIONS.IMPORT_CONTAINERS]}
          name={OPTIONS.IMPORT_CONTAINERS}
          text={`Transfer ${typeToPluralized(inputContainableType)} to new or existing containers`}
        />
        {tagSchemaId && options[OPTIONS.IMPORT_CONTAINERS] && (
          <ContainerSchemaSelector
            onChange={this._handleChangeContainerSchema}
            registryId={getTagSchema(selectedSchemaIds)!.registryId}
            selectedContainerSchemaId={containerSchemaId}
            shouldShowNoSchemaOption={false}
          />
        )}
      </div>
    );
  }
}

interface BioentityImportOptionsProps {
  enabledOptions: Array<OPTIONS>;
  options: {[key in OPTIONS]: boolean};
  selectedSchemaIds: SelectedSchemaIds;
}

class BioentityImportOptions extends React.Component<BioentityImportOptionsProps> {
  UNSAFE_componentWillReceiveProps(nextProps: BioentityImportOptionsProps): void {
    const newDisabledOptions: Array<OPTIONS> = _.difference(
      this.props.enabledOptions,
      nextProps.enabledOptions
    );
    newDisabledOptions.forEach((option) => {
      if (this.props.options[option]) {
        SpreadsheetBulkImportActions.setOptions({[option]: false});
      }
    });
  }

  shouldShowNucleotideTypeToggle(): boolean {
    const schemalessCreationEntityType = SpreadsheetBulkImportStore.getState().schemalessCreationEntityType;
    switch (schemalessCreationEntityType) {
      case ModelTypeConstants.SEQUENCE:
      case ModelTypeConstants.RNA_SEQUENCE:
      case ModelTypeConstants.DNA_OLIGO:
      case ModelTypeConstants.RNA_OLIGO:
        return true;
      default:
        return false;
    }
  }

  handleNucleotideTypeToggleChange(nucleotideType: NucleotideType): void {
    const schemalessCreationEntityType = SpreadsheetBulkImportStore.getState().schemalessCreationEntityType;
    // Long sequences stay long sequences...
    if (
      schemalessCreationEntityType === ModelTypeConstants.SEQUENCE ||
      schemalessCreationEntityType === ModelTypeConstants.RNA_SEQUENCE
    ) {
      SpreadsheetBulkImportActions.setImportSetting({
        schemalessCreationEntityType: LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[nucleotideType],
      });
    }
    // ...oligos stay oligos
    else if (
      schemalessCreationEntityType === ModelTypeConstants.DNA_OLIGO ||
      schemalessCreationEntityType === ModelTypeConstants.RNA_OLIGO
    ) {
      SpreadsheetBulkImportActions.setImportSetting({
        schemalessCreationEntityType: OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[nucleotideType],
      });
    }
  }

  render(): JSX.Element {
    const isContainersEnabled = this.props.enabledOptions.includes(OPTIONS.IMPORT_CONTAINERS);
    const schemalessCreationEntityType = SpreadsheetBulkImportStore.getState().schemalessCreationEntityType;
    return (
      <div>
        {this.shouldShowNucleotideTypeToggle() && (
          <NucleotideTypeToggle
            nucleotideType={
              schemalessCreationEntityType === ModelTypeConstants.SEQUENCE ||
              schemalessCreationEntityType === ModelTypeConstants.DNA_OLIGO
                ? NucleotideType.DNA
                : NucleotideType.RNA
            }
            onChangeSelection={(nucleotideType) => {
              this.handleNucleotideTypeToggleChange(nucleotideType);
            }}
          />
        )}
        {this.props.enabledOptions.includes(OPTIONS.ARE_SEQUENCES_CIRCULAR) && (
          <div className="u-moderate-spacing-bottom">
            <label>DNA Sequence settings</label>
            <SpreadsheetBulkImportOption
              helpContent={
                <div>
                  If this option is selected, imported DNA sequences will be circular. If not, imported DNA
                  sequences will be linear.
                </div>
              }
              isChecked={this.props.options[OPTIONS.ARE_SEQUENCES_CIRCULAR]}
              name={OPTIONS.ARE_SEQUENCES_CIRCULAR}
              text="Import circular DNA sequences"
            />
          </div>
        )}
        {isContainersEnabled && <label>Import samples</label>}
        {isContainersEnabled && (
          <ImportContainersOption
            options={this.props.options}
            selectedSchemaIds={this.props.selectedSchemaIds}
          />
        )}
      </div>
    );
  }
}

export default BioentityImportOptions;
